'use client'
import styled from '@emotion/styled'
import { theme } from '../../../../../styles/theme'
import { ComponentType } from 'react'
import { Styles, getCssProp } from '../../../../../utilities/getCssProp'
import BiologicalAge from '../../../../../assets/icons/biological-age.svg'
import BloodHealth from '../../../../../assets/icons/blood-health.svg'
import KidneyHealth from '../../../../../assets/icons/kidney-health.svg'
import Electrolytes from '../../../../../assets/icons/electrolytes.svg'
import LiverHealth from '../../../../../assets/icons/liver-health.svg'
import HeartHealth from '../../../../../assets/icons/heart-health.svg'
import VitaminsAndMinerals from '../../../../../assets/icons/vitamins-and-minerals.svg'
import HormonalHealth from '../../../../../assets/icons/hormonal-health.svg'
import MetabolicHealth from '../../../../../assets/icons/metabolic-health.svg'
import ThyroidHealth from '../../../../../assets/icons/thyroid-health.svg'
import FattyAcids from '../../../../../assets/icons/fatty-acids.svg'
import HeavyMetals from '../../../../../assets/icons/heavy-metals.svg'
import { DropdownBase, UseTooltipOptions, useTooltip } from '../../../../../common/components/InfoTooltip/useTooltip'
import { useOnClickOutside } from '../../../../../hooks/useOnClickOutside'
import { isNode } from '../../../../../typescript/guards/isNode'
import { useDevice } from '../../../../../hooks/useDevice'
import { Arrow, getPopperPlacement } from '../../../../../common/components/InfoTooltip/InfoTooltip'
import { transientOptions } from '../../../../../common/style/transientOptions'
import { accessibleNoninteractiveElementClick } from '../../../../../common/a11y/accessibleNoninteractiveElementClick'

type ContainerProps = {
  $isOnDarkBackground?: boolean
  $isActive: boolean
  $svgColor: string
  $isHormonalHealth?: boolean
}
const Container = styled('div', transientOptions)<ContainerProps>`
  border-radius: 4px;
  width: 24px;
  height: 24px;
  transition: background-color 0.3s;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${(p) =>
    p.$isActive ? (p.$isOnDarkBackground ? theme.colors.primary300 : theme.colors.grayscale000) : 'none'};

  :not(:first-of-type) {
    margin-left: 8px;
  }

  path,
  circle {
    stroke: ${(p) => p.$svgColor};
  }

  path:last-of-type {
    stroke: ${(p) => (p.$isHormonalHealth ? 'transparent' : p.$svgColor)};
    fill: ${(p) => (p.$isHormonalHealth ? p.$svgColor : 'transparent')};
  }
`

const categoryToSvgNameDict: Record<string, ComponentType<{ width: number; height: number }>> = {
  'Biological Age': BiologicalAge,
  'Blood Health': BloodHealth,
  'Kidney Health': KidneyHealth,
  Electrolytes: Electrolytes,
  'Liver Health': LiverHealth,
  'Heart Health': HeartHealth,
  'Vitamins & Minerals': VitaminsAndMinerals,
  'Hormonal Health': HormonalHealth,
  'Metabolic Health': MetabolicHealth,
  'Thyroid Health': ThyroidHealth,
  'Fatty Acids': FattyAcids,
  'Heavy Metals': HeavyMetals,
}

type Props = {
  category: string
  styles?: Styles
  isInteractive?: boolean
  isOnDarkBackground?: boolean
  useTooltipOptions?: UseTooltipOptions
  color?: string
}

const DropDown = styled(DropdownBase)`
  padding: 6px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.06px;
  color: ${theme.colors.grayscale700};
  background: ${theme.colors.grayscalewhite};
  border-radius: 8px;
  z-index: 1;
  box-shadow:
    0px 0px 11.16338px 0px rgba(111, 140, 176, 0.25),
    2.23268px 2.23268px 11.16338px 0px rgba(111, 140, 176, 0.41),
    -2.23268px -2.23268px 5.02352px 0px rgba(255, 255, 255, 0.88) inset;
`

export const CategoryIcon = ({
  category,
  styles,
  isOnDarkBackground = false,
  isInteractive = true,
  useTooltipOptions,
  color = theme.colors.primary700,
}: Props) => {
  const Svg = categoryToSvgNameDict[category]

  const { canHover } = useDevice()
  const { ownerProps, tooltipProps, setShow, referenceElement, arrowProps, show } = useTooltip(useTooltipOptions)

  const { ref: dropdownRef } = useOnClickOutside((e) => {
    if (e.target !== referenceElement && (!isNode(e.target) || !referenceElement?.contains(e.target))) setShow(false)
  })

  if (!Svg) return null

  const cssProp = getCssProp(styles)

  return (
    <div>
      <Container
        $isHormonalHealth={category === 'Hormonal Health'}
        $svgColor={color}
        $isOnDarkBackground={isOnDarkBackground}
        $isActive={show && isInteractive}
        key={category}
        css={cssProp}
        {...ownerProps}
        onMouseEnter={() => isInteractive && canHover && setShow(true)}
        onMouseLeave={() => isInteractive && canHover && setShow(false)}
        {...(isInteractive &&
          accessibleNoninteractiveElementClick((e) => {
            e.stopPropagation()
            e.preventDefault()
            setShow((s) => !s)
          }))}
      >
        <Svg width={24} height={24} />
      </Container>
      {isInteractive && (
        <span ref={dropdownRef}>
          <DropDown {...tooltipProps}>
            <Arrow {...arrowProps} $popperPlacement={getPopperPlacement(tooltipProps)} />
            {category}
          </DropDown>
        </span>
      )}
    </div>
  )
}
